import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCardDetails, postComment, uploadAttachment } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AttachButton from '../components/AttachButton';
import logo from '../titan-logo.jpg';

// Trello attachment display — uses direct img src (no fetch, avoids CORS)
// Trello's /cards/{id}/attachments/{attId}/download endpoint serves images
// with proper CORS headers when credentials are in the URL
function AttachmentThumb({ att }) {
  const [failed, setFailed] = useState(false);
  const key   = process.env.REACT_APP_TRELLO_API_KEY;
  const token = process.env.REACT_APP_TRELLO_TOKEN;

  const isImage = att.mimeType?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(att.name || '');

  // Use the Trello card attachment download endpoint with auth in query string
  // This works as an img src because browsers don't send CORS preflight for images
  const authParams = `key=${key}&token=${token}`;

  // Pick smallest preview >= 150px, or fall back to full attachment URL
  const preview = att.previews
    ?.filter(p => p.width >= 150)
    ?.sort((a, b) => a.width - b.width)[0];

  // For the thumbnail: use preview URL if available, else the attachment URL
  // Both need auth params appended
  const thumbUrl = preview
    ? `${preview.url}?${authParams}`
    : isImage ? `${att.url}?${authParams}` : null;

  const openUrl = `${att.url}?${authParams}`;

  return (
    <a href={openUrl} target="_blank" rel="noreferrer" className="attachment-item">
      {isImage && thumbUrl && !failed ? (
        <img
          src={thumbUrl}
          alt={att.name}
          onError={() => setFailed(true)}
          crossOrigin="use-credentials"
        />
      ) : (
        <div className="attachment-file">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4 4h8l4 4v8a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1Z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
            <path d="M12 4v4h4" stroke="currentColor" strokeWidth="1.2"/>
          </svg>
          <span>{att.name?.slice(0, 18)}</span>
        </div>
      )}
    </a>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { worker } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef();
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!worker) { navigate('/'); return; }
    loadCard();
  }, [id]);

  async function loadCard() {
    setLoading(true);
    try {
      const data = await getCardDetails(id);
      setCard(data);
    } catch {
      showToast('Failed to load job details', 'error');
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function submitNote() {
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      const text = `[${worker.fullName}] ${noteText.trim()}`;
      await postComment(id, text);
      setNoteText('');
      showToast('Note posted to Trello');
      loadCard();
    } catch {
      showToast('Failed to post note', 'error');
    } finally {
      setPosting(false);
    }
  }

  async function handlePhoto(file) {
    if (!file) return;
    setUploading(true);
    try {
      await uploadAttachment(id, file);
      showToast('File uploaded');
      loadCard();
    } catch {
      showToast('Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }

  function startVoice() {
    try {
      const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRec) {
        showToast('Voice not supported on this browser', 'error');
        return;
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
        recognitionRef.current = null;
      }
      const rec = new SpeechRec();
      rec.continuous = false;
      rec.interimResults = false;
      rec.lang = 'en-US';
      rec.onresult = (e) => {
        try {
          const transcript = e.results[0]?.[0]?.transcript || '';
          if (transcript) setNoteText(prev => prev ? prev + ' ' + transcript : transcript);
        } catch {}
        setListening(false);
      };
      rec.onerror = (e) => {
        setListening(false);
        if (e.error !== 'aborted') showToast('Voice input failed', 'error');
      };
      rec.onend = () => setListening(false);
      recognitionRef.current = rec;
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
      showToast('Could not start voice input', 'error');
    }
  }

  function stopVoice() {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  }

  function getProgress() {
    if (!card?.checklists?.length) return null;
    const total = card.checklists.reduce((a, cl) => a + (cl.checkItems?.length || 0), 0);
    const done = card.checklists.reduce((a, cl) => a + (cl.checkItems?.filter(i => i.state === 'complete').length || 0), 0);
    return total > 0 ? { done, total, pct: Math.round((done / total) * 100) } : null;
  }

  const progress = getProgress();
  const comments = card?.actions?.filter(a => a.type === 'commentCard') || [];

  if (loading) return (
    <div className="page">
      <div className="center-message" style={{ marginTop: 80 }}>
        <div className="spinner" /><span>Loading job…</span>
      </div>
    </div>
  );

  if (!card) return null;

  return (
    <div className="page">
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      <div className="detail-hero">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Back
        </button>
        <div className="detail-status">{card.listName || 'In Progress'}</div>
        <h2 className="detail-title">{card.name}</h2>
        {card.due && (
          <div className="detail-due">Due {new Date(card.due).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
        )}
      </div>

      {progress && (
        <div className="detail-section">
          <div className="detail-label">PROGRESS — {progress.done}/{progress.total} tasks</div>
          <div className="progress-bar" style={{ marginTop: 6 }}>
            <div className="progress-fill" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      )}

      {card.desc && (
        <div className="detail-section">
          <div className="detail-label">DESCRIPTION</div>
          <p className="detail-desc">{card.desc}</p>
        </div>
      )}

      {card.members?.length > 0 && (
        <div className="detail-section">
          <div className="detail-label">CREW</div>
          <div className="crew-row">
            {card.members.map(m => (
              <div key={m.id} className="crew-chip">
                {m.avatarUrl
                  ? <img src={`${m.avatarUrl}/30.png`} alt={m.fullName} />
                  : <span>{m.fullName?.split(' ').map(n=>n[0]).join('').slice(0,2)}</span>
                }
                <span>{m.fullName?.split(' ')[0]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {card.checklists?.length > 0 && (
        <div className="detail-section">
          <div className="detail-label">CHECKLISTS</div>
          {card.checklists.map(cl => (
            <div key={cl.id} className="checklist">
              <div className="checklist-name">{cl.name}</div>
              {cl.checkItems?.map(item => (
                <div key={item.id} className={`check-item ${item.state === 'complete' ? 'done' : ''}`}>
                  <span className="check-box">{item.state === 'complete' ? '✓' : ''}</span>
                  {item.name}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {card.attachments?.length > 0 && (
        <div className="detail-section">
          <div className="detail-label">ATTACHMENTS ({card.attachments.length})</div>
          <div className="attachment-grid">
            {card.attachments.map(att => (
              <AttachmentThumb key={att.id} att={att} />
            ))}
          </div>
        </div>
      )}

      {comments.length > 0 && (
        <div className="detail-section">
          <div className="detail-label">NOTES ({comments.length})</div>
          {comments.slice(0, 5).map(action => (
            <div key={action.id} className="comment">
              <div className="comment-meta">
                {action.memberCreator?.fullName} · {new Date(action.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="comment-text">{action.data?.text}</div>
            </div>
          ))}
        </div>
      )}

      <div className="note-input-section">
        <div className="detail-label" style={{ padding: '0 0 8px' }}>ADD A NOTE</div>
        <textarea
          className="note-textarea"
          placeholder="Type your update here…"
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          rows={3}
        />
        <div className="note-actions">
          <button
            className={`action-btn ${listening ? 'btn-recording' : ''}`}
            onClick={listening ? stopVoice : startVoice}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="5" y="1" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.3" fill={listening ? 'currentColor' : 'none'}/>
              <path d="M2 8c0 3.3 2.7 6 6 6s6-2.7 6-6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
              <path d="M8 14v1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            {listening ? 'Stop' : 'Speak'}
          </button>
          <AttachButton variant="plain" icon="📷" label={uploading ? 'Uploading…' : 'Photo / File'} disabled={uploading}
            style={{ padding: '8px 12px', fontSize: 13 }} onFiles={fs => handlePhoto(fs[0])} />
          <button className="action-btn btn-primary" onClick={submitNote} disabled={posting || !noteText.trim()}>
            {posting ? 'Posting…' : 'Post note'}
          </button>
        </div>
      </div>

      <div style={{ height: 40 }} />
    </div>
  );
}
